import { useState, useEffect } from 'react';
import { productsAPI } from '../lib/api';
import ProductCard from '../components/ProductCard';
import Seo from '../components/Seo';
import { ArrowRight, Search, SlidersHorizontal } from 'lucide-react';
import { SECTORS } from '../data/sectors';
import PerforatedDivider from '../components/PerforatedDivider';
import { bestPricePerKgId } from '../lib/highlights';
import { Link } from 'react-router-dom';
import { ARTICLES } from '../data/articles';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

/**
 * Preguntas frecuentes reales del catálogo — cada respuesta está tomada o
 * derivada directamente del contenido ya publicado en las guías (no son
 * afirmaciones nuevas): sirve tanto para el FAQPage schema como para el
 * bloque visible debajo. El schema DEBE coincidir con lo que el usuario ve,
 * nunca inventarse aparte — Google trata la discrepancia como riesgo de
 * acción manual.
 */
// Las tres guías que responden la decisión de compra desde el catálogo.
// Por slug, no por posición: si mañana se agrega un artículo al principio de
// articles.js, aquí no cambia nada.
const GUIAS_DESTACADAS = [
    'como-elegir-el-gramaje-correcto',
    'papel-encerado-vs-estraza',
    'cuanto-papel-necesita-tu-negocio',
].map((slug) => ARTICLES.find((a) => a.slug === slug)).filter(Boolean);

const FAQ = [
    {
        q: '¿Qué es el papel grado alimenticio?',
        a: 'Es papel fabricado específicamente para entrar en contacto directo con alimentos, sin tintas ni blanqueadores que puedan migrar al producto. Todos los papeles de Carpepel son grado alimenticio. Si necesitas la ficha técnica o documentación de un producto, pídela por WhatsApp.',
    },
    {
        q: '¿Cuál es la diferencia entre papel encerado y papel estraza?',
        a: 'El estraza es papel kraft sin tratamiento: resiste la manipulación pero se transparenta con la grasa. El encerado lleva un tratamiento de cera que crea una barrera contra grasa y humedad. Elige estraza para productos secos y encerado para alimentos con grasa visible como tacos o hamburguesas.',
    },
    {
        q: '¿Qué gramaje de papel necesito para mi negocio?',
        a: 'Depende del peso y la grasa del producto: 16–18g para productos ligeros y secos, 24–30g para uso general (tacos, tortas, antojitos), y 40–49g para producto pesado, húmedo o muy grasoso.',
    },
    {
        q: '¿Carpepel vende al mayoreo y al menudeo?',
        a: 'Sí, Carpepel vende tanto al mayoreo como al menudeo, con precios especiales por volumen para negocios.',
    },
    {
        q: '¿A qué zonas hace envíos Carpepel?',
        a: 'Carpepel envía a toda la República Mexicana, y también a Estados Unidos, Canadá y Centroamérica. Los pedidos nacionales tienen dos opciones: pagar solo el papel y coordinar el envío por WhatsApp, donde se cotiza y se cobra aparte, o cotizar el envío con tu código postal y pagar todo junto; los internacionales los cotizamos por WhatsApp, porque el costo depende del país, del peso y del trámite de aduana.',
    },
    {
        q: '¿Cómo sé qué papel elegir si no estoy seguro?',
        a: 'Escríbenos por WhatsApp contando qué vendes y te decimos exactamente qué papel usar según tu producto.',
    },
    {
        q: '¿Qué norma NOM certifica el papel grado alimenticio en México?',
        a: 'No existe una NOM específica para la composición del papel, a diferencia de los plásticos. Aplica la NOM-251-SSA1-2009 de prácticas de higiene, que exige que cualquier material en contacto con alimentos sea no tóxico; la conformidad se respalda con documentación del proveedor sobre origen de pulpa y tratamiento.',
    },
    {
        q: '¿Qué es el papel mostaza y para qué se usa?',
        a: 'Es papel grado alimenticio de 29 gramos en medida 35×50 cm, del mismo gramaje que el papel oro — pensado para uso general en tortillerías, taquerías y negocios de comida que buscan una presentación de color distinta. Se vende por paca de 9 kg, al mayoreo y menudeo.',
    },
];

/**
 * ¿Es una bolsa y no papel? `category` es texto libre en el catálogo real
 * ('bolsas_poliseda', 'Plásticos de poliseda presentación de 1 kilo'…), así
 * que no se compara con un valor exacto: se busca la palabra. La regla de
 * data/sectors.js compara exacto y por eso no ve la «Bolsa de asas»; no se
 * toca aquí porque cambiaría qué productos listan las páginas de giro.
 */
const esBolsa = (p) => /poliseda|pl[aá]stic|bolsa/i.test(`${p.category || ''} ${p.name || ''}`);

/**
 * Orden por defecto: el papel primero, de más ligero a más pesado, y las
 * bolsas al final. Con A-Z la «Bolsa de asas» abría el catálogo de una
 * tienda de papel y el papel quedaba varias pantallas más abajo. El gramaje
 * ascendente sigue la misma escala que enseña la guía de cómo elegirlo.
 */
function recomendado(a, b) {
    const ba = esBolsa(a), bb = esBolsa(b);
    if (ba !== bb) return ba ? 1 : -1;
    const ga = a.weight_grams || 0, gb = b.weight_grams || 0;
    return ga - gb || a.name.localeCompare(b.name);
}

const FILTER_BOLSAS = 'bolsas';

export default function Catalog() {
    const [products, setProducts] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('recommended');
    // 'all', el slug de un giro (misma clasificación que las páginas de
    // sector) o 'bolsas'.
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const bestPriceId = bestPricePerKgId(products);

    useEffect(() => {
        productsAPI.getAll().then(res => {
            setProducts(res.data);
            setFiltered(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    useEffect(() => {
        let result = [...products];
        if (filter === FILTER_BOLSAS) {
            result = result.filter(esBolsa);
        } else if (filter !== 'all') {
            const sector = SECTORS.find((x) => x.slug === filter);
            if (sector) result = result.filter((p) => !esBolsa(p) && sector.matches(p));
        }
        if (search) {
            result = result.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
        }
        if (sortBy === 'price_asc') result.sort((a, b) => a.price - b.price);
        else if (sortBy === 'price_desc') result.sort((a, b) => b.price - a.price);
        else if (sortBy === 'weight') result.sort((a, b) => a.weight_grams - b.weight_grams);
        else if (sortBy === 'name') result.sort((a, b) => a.name.localeCompare(b.name));
        else result.sort(recomendado);
        setFiltered(result);
    }, [search, sortBy, filter, products]);

    // Solo se ofrecen filtros que dejan al menos un producto: un chip que
    // lleva a una pantalla vacía es peor que no tenerlo. Los giros cuentan
    // sin bolsas, igual que al filtrar.
    const chips = [
        { id: 'all', label: 'Todo', count: products.length },
        ...SECTORS.map((x) => ({ id: x.slug, label: x.label, count: products.filter((p) => !esBolsa(p) && x.matches(p)).length })),
        { id: FILTER_BOLSAS, label: 'Bolsas', count: products.filter(esBolsa).length },
    ].filter((c) => c.id === 'all' || c.count > 0);

    return (
        <div data-testid="catalog-page">
            <Seo
                title="Catálogo de Papel Grado Alimenticio por Gramaje | Carpepel"
                description="Explora el catálogo completo de papel grado alimenticio por gramaje y precio: amarillo, oro, mostaza, canela y tissue. Compra al mayoreo o menudeo online."
                keywords={["papel para tortillas mayoreo", "papel grado alimenticio mayoreo", "papel de grado alimenticio", "papel mostaza", "papel para tortillas", "papel para tacos", "papel para comida", "papel grado alimenticio", "insumos alimenticios", "papel al mayoreo", "papel encerado", "papel estraza"]}
                canonicalPath="/catalogo"
                schema={[
                    {
                        '@type': 'ItemList',
                        name: 'Catálogo de papel grado alimenticio Carpepel',
                        itemListElement: products.map((p, i) => ({
                            '@type': 'ListItem',
                            position: i + 1,
                            url: `https://www.carpepel.com/producto/${p.id}`,
                            name: p.name,
                        })),
                    },
                    {
                        '@type': 'FAQPage',
                        mainEntity: FAQ.map(({ q, a }) => ({
                            '@type': 'Question',
                            name: q,
                            acceptedAnswer: { '@type': 'Answer', text: a },
                        })),
                    },
                    {
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://www.carpepel.com/' },
                            { '@type': 'ListItem', position: 2, name: 'Catálogo', item: 'https://www.carpepel.com/catalogo' },
                        ],
                    },
                ]}
            />
            {/* Page header — coherente con hero bar */}
            {/* Mismo sistema que la portada: rótulo con regla, titular fluido y
                la marca de agua cortada. Es el encabezado de la página que más
                visitas de compra recibe y era el bloque más plano del sitio. */}
            <div style={{ backgroundColor: 'var(--ink)' }} className="on-ink relative overflow-hidden w-full pt-10 pb-14 md:pt-12 md:pb-20">
                <span className="watermark" aria-hidden="true">Catálogo</span>
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <p className="eyebrow mb-4">Papel grado alimenticio</p>
                    <h1 className="font-heading text-[clamp(2.25rem,5vw,3.75rem)] font-black text-white leading-[0.98] tracking-tight">
                        NUESTRO <span style={{ color: 'var(--kraft)' }}>CATÁLOGO</span>
                    </h1>
                    <p className="mt-4 text-sm max-w-xl" style={{ color: 'var(--ink-dim)' }} data-testid="catalog-buy-note">
                        Agrega lo que necesitas y paga con tarjeta, <strong className="font-bold" style={{ color: 'var(--paper)' }}>sin crear cuenta</strong>. El envío lo cotizas en el carrito o lo coordinamos por WhatsApp.
                    </p>
                </div>
            </div>

            <div className="folio-sections max-w-7xl mx-auto px-6 md:px-12 py-10">

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-soft)]" />
                    <Input
                        data-testid="catalog-search"
                        placeholder="Buscar producto..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 border-2 border-[var(--line)] focus:border-[var(--kraft-deep)] rounded-sm h-12 font-medium"
                    />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger data-testid="catalog-sort" className="w-full sm:w-52 border-2 border-[var(--line)] rounded-sm h-12">
                        <SlidersHorizontal className="w-4 h-4 mr-2 text-[var(--ink-soft)]" />
                        <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm">
                        <SelectItem value="recommended">Recomendado</SelectItem>
                        <SelectItem value="name">Nombre A-Z</SelectItem>
                        <SelectItem value="price_asc">Precio: Menor</SelectItem>
                        <SelectItem value="price_desc">Precio: Mayor</SelectItem>
                        <SelectItem value="weight">Gramaje</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Filtro por giro: la misma clasificación por gramaje de las
                páginas «Papel para…». En celular es una sola fila que se
                desliza; un wrap de seis botones se comía media pantalla. */}
            {!loading && products.length > 0 && (
                <div className="-mx-6 md:mx-0 mb-8 -mt-4" data-testid="catalog-filters">
                    <p className="px-6 md:px-0 mb-2 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--ink-soft)' }}>
                        Para tu negocio
                    </p>
                    <div className="flex gap-2 overflow-x-auto md:flex-wrap px-6 md:px-0 pb-1 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {chips.map((c) => {
                            const on = filter === c.id;
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    aria-pressed={on}
                                    data-testid={`filter-${c.id}`}
                                    onClick={() => setFilter(c.id)}
                                    className="snap-start shrink-0 h-10 px-4 border-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors duration-200 whitespace-nowrap"
                                    style={{
                                        borderColor: on ? 'var(--ink)' : 'var(--line)',
                                        backgroundColor: on ? 'var(--ink)' : 'transparent',
                                        color: on ? 'var(--paper)' : 'var(--ink-soft)',
                                    }}
                                >
                                    {c.label}
                                    <span className="ml-1.5 font-mono font-normal opacity-70">{c.count}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Products Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1,2,3,4,5,6].map(i => (
                        <div key={i} className="h-64 bg-[var(--line)] animate-pulse rounded-sm" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20">
                    <p className="text-[var(--ink-soft)] text-base">No se encontraron productos</p>
                    {(filter !== 'all' || search) && (
                        <button
                            type="button"
                            onClick={() => { setFilter('all'); setSearch(''); }}
                            className="mt-4 text-sm font-bold underline"
                            style={{ color: 'var(--kraft-deep)' }}
                            data-testid="catalog-clear-filters"
                        >
                            Quitar filtros
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((product, i) => (
                        <ProductCard
                            key={product.id}
                            product={product}
                            index={i}
                            /* La primera fila de esta página es lo primero que
                               se ve, y su foto es el elemento LCP medido. Tres
                               porque en escritorio la fila son tres tarjetas. */
                            priority={i < 3}
                            /* Sobre `products` (todo el catálogo), no sobre
                               `filtered`: si no, al buscar o filtrar la etiqueta
                               saltaría al más barato de lo que quedó en pantalla
                               y diría algo falso. */
                            ribbon={product.id === bestPriceId ? 'Mejor precio por kilo' : null}
                        />
                    ))}
                </div>
            )}

            {/* Product count */}
            <div className="mt-8 font-mono text-[11px] uppercase tracking-[0.14em]" style={{ color: 'var(--ink-soft)' }}>
                {filtered.length} producto{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
            </div>

            {/* Cotización de mayoreo — mismo número/patrón que WhatsAppButton,
                mensaje distinto porque aquí la intención es volumen, no soporte. */}
            <div
                style={{ backgroundColor: 'var(--ink)' }}
                className="on-ink relative overflow-hidden mt-16 rounded-sm px-8 pt-10 pb-24 md:px-12 md:pt-12 md:pb-36 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
                data-testid="mayoreo-cta"
            >
                <span className="watermark" aria-hidden="true">Mayoreo</span>
                <div className="relative">
                    <h2 className="font-heading text-2xl md:text-4xl font-black uppercase tracking-tight leading-[0.98] text-white mb-3 text-balance">
                        ¿Compras al mayoreo?
                    </h2>
                    <p className="text-sm" style={{ color: 'var(--ink-dim)' }}>
                        Precios especiales por volumen para tortillerías, restaurantes y negocios de comida. Cotiza directo por WhatsApp.
                    </p>
                </div>
                <a
                    href={`https://wa.me/525584032954?text=${encodeURIComponent('Hola, quiero una cotización de mayoreo de papel grado alimenticio.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mayoreo-cta-link"
                    className="relative inline-flex items-center gap-2 h-12 px-8 rounded-sm uppercase font-bold tracking-widest text-sm bg-[var(--kraft)] text-[var(--ink)] hover:opacity-85 whitespace-nowrap"
                >
                    Cotizar por WhatsApp
                </a>
            </div>

            {/* Preguntas frecuentes — lista editorial con regla horizontal, el
                mismo patrón ya usado en Guías. El texto aquí es exactamente el
                que describe el FAQPage schema de arriba, palabra por palabra. */}
            <PerforatedDivider className="mt-16" />
            <section className="pt-12" data-testid="catalog-faq">
                <p className="eyebrow mb-4">Antes de comprar</p>
                <h2 className="font-heading text-2xl md:text-3xl font-black mb-8 text-balance" style={{ color: 'var(--ink)' }}>
                    Preguntas frecuentes
                </h2>
                <div className="border-t" style={{ borderColor: 'var(--line)' }}>
                    {FAQ.map(({ q, a }) => (
                        <div key={q} className="reveal-on-scroll grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-10 py-6 border-b" style={{ borderColor: 'var(--line)' }}>
                            <h3 className="md:col-span-4 font-heading text-base md:text-lg font-bold" style={{ color: 'var(--ink)' }}>
                                {q}
                            </h3>
                            <p className="md:col-span-8 text-[15px] leading-relaxed text-pretty" style={{ color: 'var(--ink-soft)' }}>
                                {a}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Guías desde el catálogo: medido, esta página enlazaba a CERO.
                Es la que más visitas de compra recibe y el contenido que
                resuelve la duda previa ("¿qué gramaje necesito?") no tenía
                ninguna ruta desde aquí — ni para quien compra ni para el
                rastreador, que así nunca veía las guías enlazadas desde una
                página fuerte. Se eligen por slug y no por los tres primeros
                del arreglo: son las tres que responden la decisión de compra. */}
            <section className="pt-12" data-testid="catalog-guides">
                <p className="eyebrow mb-6">Guías para elegir bien</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {GUIAS_DESTACADAS.map((g) => (
                        <Link
                            key={g.slug}
                            to={`/guias/${g.slug}`}
                            className="group block p-5 border rounded-sm"
                            style={{ borderColor: 'var(--line)', backgroundColor: 'var(--paper-2)' }}
                        >
                            <span className="block font-heading font-bold text-base leading-snug" style={{ color: 'var(--ink)' }}>
                                {g.title}
                            </span>
                            <span className="block text-xs leading-relaxed mt-2" style={{ color: 'var(--ink-soft)' }}>
                                {g.excerpt}
                            </span>
                            <span className="inline-flex items-center gap-1.5 mt-3 font-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: 'var(--kraft-deep)' }}>
                                Leer la guía
                                <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-0.5" />
                            </span>
                        </Link>
                    ))}
                </div>
            </section>
            </div>
        </div>
    );
}
